import { useState } from "react";
import { Calculator, ArrowRight, Car, Home, Heart, Building2, Bike, Plane, Shield, Smartphone, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "react-router-dom";

const types = [
  { id: "auto", label: "Auto", icon: Car, valueLabel: "Valor do veículo (R$)", placeholder: "Ex: 80.000" },
  { id: "moto", label: "Moto", icon: Bike, valueLabel: "Valor da moto (R$)", placeholder: "Ex: 25.000" },
  { id: "residencial", label: "Residencial", icon: Home, valueLabel: "Valor do imóvel (R$)", placeholder: "Ex: 400.000" },
  { id: "vida", label: "Vida", icon: Heart, valueLabel: "Cobertura desejada (R$)", placeholder: "Ex: 200.000" },
  { id: "empresarial", label: "Empresarial", icon: Building2, valueLabel: "Valor do patrimônio (R$)", placeholder: "Ex: 500.000" },
  { id: "viagem", label: "Viagem", icon: Plane, valueLabel: "Dias de viagem", placeholder: "Ex: 15" },
  { id: "celular", label: "Celular", icon: Smartphone, valueLabel: "Valor do celular (R$)", placeholder: "Ex: 5.000" },
  { id: "caminhao", label: "Caminhão", icon: Truck, valueLabel: "Valor do caminhão (R$)", placeholder: "Ex: 350.000" },
  { id: "cyber", label: "Riscos Cibernéticos", icon: Shield, valueLabel: "Faturamento anual (R$)", placeholder: "Ex: 1.000.000" },
];

const states = [
  "GO - Goiás", "MG - Minas Gerais", "SP - São Paulo", "RJ - Rio de Janeiro",
  "DF - Distrito Federal", "PR - Paraná", "SC - Santa Catarina", "RS - Rio Grande do Sul",
  "BA - Bahia", "PE - Pernambuco", "CE - Ceará", "PA - Pará",
  "MA - Maranhão", "AM - Amazonas", "ES - Espírito Santo", "MT - Mato Grosso",
  "MS - Mato Grosso do Sul", "AL - Alagoas", "RN - Rio Grande do Norte",
  "PB - Paraíba", "SE - Sergipe", "PI - Piauí", "TO - Tocantins",
  "RO - Rondônia", "AC - Acre", "AP - Amapá", "RR - Roraima",
];

function estimate(type, value, state) {
  const v = parseFloat(value) || 0;
  if (v <= 0) return null;

  const stateMultiplier = state?.includes("SP") ? 1.35
    : state?.includes("RJ") ? 1.30
    : state?.includes("MG") ? 1.10
    : state?.includes("GO") ? 1.05
    : state?.includes("DF") ? 1.20
    : state?.includes("PR") || state?.includes("RS") || state?.includes("SC") ? 1.08
    : state?.includes("BA") || state?.includes("PE") || state?.includes("CE") ? 1.12
    : 1.0;

  let low, high, suffix = "/ano", note = "";

  switch (type) {
    case "auto":
      low = v * 0.028 * stateMultiplier;
      high = v * 0.055 * stateMultiplier;
      low = Math.max(low, 1200); high = Math.max(high, 2400);
      note = "Varia conforme perfil, CEP e modelo";
      break;
    case "moto":
      low = v * 0.04 * stateMultiplier;
      high = v * 0.08 * stateMultiplier;
      low = Math.max(low, 600); high = Math.max(high, 1500);
      note = "Motos esportivas podem ter valores maiores";
      break;
    case "residencial":
      low = v * 0.001 * stateMultiplier;
      high = v * 0.004 * stateMultiplier;
      low = Math.max(low, 250); high = Math.max(high, 600);
      note = "Inclui assistência 24h (chaveiro, encanador, etc.)";
      break;
    case "vida":
      low = v * 0.0004;
      high = v * 0.0015;
      low = Math.max(low, 40); high = Math.max(high, 150);
      suffix = "/mês";
      note = "Valor varia conforme idade e coberturas adicionais";
      break;
    case "empresarial":
      low = v * 0.002 * stateMultiplier;
      high = v * 0.006 * stateMultiplier;
      low = Math.max(low, 800); high = Math.max(high, 2500);
      note = "Depende do ramo de atividade e localização";
      break;
    case "viagem":
      low = v * 12;
      high = v * 45;
      low = Math.max(low, 80); high = Math.max(high, 300);
      suffix = " total";
      note = "Destino internacional tem valores maiores";
      break;
    case "celular":
      low = v * 0.08;
      high = v * 0.18;
      low = Math.max(low, 150); high = Math.max(high, 500);
      note = "Cobre roubo, furto, queda e dano por líquido";
      break;
    case "caminhao":
      low = v * 0.025 * stateMultiplier;
      high = v * 0.050 * stateMultiplier;
      low = Math.max(low, 5000); high = Math.max(high, 15000);
      note = "Inclui cobertura RCF-DC e APP";
      break;
    case "cyber":
      low = v * 0.003;
      high = v * 0.012;
      low = Math.max(low, 3000); high = Math.max(high, 10000);
      note = "Cobertura LGPD, ransomware e vazamento de dados";
      break;
    default: return null;
  }

  return {
    low: Math.round(low),
    high: Math.round(high),
    suffix,
    note,
    monthly: suffix === "/ano" ? { low: Math.round(low / 12), high: Math.round(high / 12) } : null,
  };
}

export default function InsuranceCalculator() {
  const [type, setType] = useState("");
  const [value, setValue] = useState("");
  const [state, setState] = useState("");
  const [result, setResult] = useState(null);

  const handleCalc = () => {
    const r = estimate(type, value, state);
    setResult(r);
  };

  const currentType = types.find(t => t.id === type);
  const needsState = !["viagem", "celular", "cyber"].includes(type);

  return (
    <div data-testid="insurance-calculator">
      <div className="grid md:grid-cols-2 gap-10 items-start">
        <div>
          <p className="text-sm font-semibold text-gold uppercase tracking-wider mb-3">Calculadora de Seguros</p>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy mb-4">Estime o valor do seu seguro em segundos</h2>
          <p className="text-base text-slate-500 leading-relaxed mb-6">
            Use nossa calculadora para ter uma estimativa rápida. Para um valor exato e personalizado, solicite uma cotação gratuita com nossos consultores.
          </p>
          <div className="flex flex-wrap gap-2">
            {types.map((t) => (
              <button
                key={t.id}
                onClick={() => { setType(t.id); setResult(null); setValue(""); }}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${type === t.id ? "bg-brand text-white border-brand shadow-md" : "bg-white text-slate-600 border-slate-200 hover:border-brand/40"}`}
                data-testid={`calc-type-${t.id}`}
              >
                <t.icon className="h-3.5 w-3.5" /> {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 shadow-sm">
          {!type ? (
            <div className="text-center py-8">
              <Calculator className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">Selecione o tipo de seguro para começar</p>
            </div>
          ) : (
            <div className="space-y-4">
              <h3 className="font-heading font-semibold text-navy text-lg mb-1">
                {currentType?.label}
              </h3>
              <div>
                <label className="text-sm text-slate-600 mb-1.5 block">{currentType?.valueLabel}</label>
                <Input
                  type="number"
                  placeholder={currentType?.placeholder}
                  value={value}
                  onChange={(e) => { setValue(e.target.value); setResult(null); }}
                  className="h-11"
                  data-testid="calc-value-input"
                />
              </div>
              {needsState && (
                <div>
                  <label className="text-sm text-slate-600 mb-1.5 block">Estado</label>
                  <Select value={state} onValueChange={(v) => { setState(v); setResult(null); }}>
                    <SelectTrigger className="h-11" data-testid="calc-state-select"><SelectValue placeholder="Selecione o estado" /></SelectTrigger>
                    <SelectContent>
                      {states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Button onClick={handleCalc} className="btn-gold w-full h-11" data-testid="calc-btn">
                <Calculator className="h-4 w-4 mr-2" /> Calcular Estimativa
              </Button>

              {result && (
                <div className="mt-4 bg-brand/5 border border-brand/20 rounded-xl p-5" data-testid="calc-result">
                  <div className="text-center mb-3">
                    <p className="text-sm text-slate-500 mb-1">Estimativa de valor</p>
                    <p className="text-2xl font-heading font-bold text-brand">
                      R$ {result.low.toLocaleString("pt-BR")} — R$ {result.high.toLocaleString("pt-BR")}
                      <span className="text-sm font-normal text-slate-500 ml-1">{result.suffix}</span>
                    </p>
                    {result.monthly && (
                      <p className="text-sm text-slate-500 mt-1">
                        ou R$ {result.monthly.low.toLocaleString("pt-BR")} — R$ {result.monthly.high.toLocaleString("pt-BR")}/mês
                      </p>
                    )}
                  </div>
                  {result.note && (
                    <p className="text-xs text-slate-400 text-center mb-4">*{result.note}</p>
                  )}
                  <Link to="/cotacao">
                    <Button className="btn-gold h-10 px-6 text-sm w-full" data-testid="calc-cta">
                      Solicitar Cotação Exata <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
